import {
  ArrowLeft,
  Briefcase,
  CheckCircle,
  Clock,
  Download,
  FileText,
  Mail,
  Phone,
  Send,
  Upload,
  User,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLanguage } from "../../context/LanguageContext";
import { adeverintaAPI } from "../../lib/api";
import AdminLayout from "./AdminLayout";

const AdminAdeverintaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchItem = useCallback(async () => {
    try {
      const data = await adeverintaAPI.getById(id);
      setItem(data);
    } catch (error) {
      toast.error("Failed to fetch certificate details");
      navigate("/admin/adeverinta");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      toast.error("Please select a PDF file");
    }
  };

  const handleUploadAndSend = async () => {
    if (!selectedFile) {
      toast.error("Please select a PDF file");
      return;
    }

    setUploading(true);
    try {
      await adeverintaAPI.uploadSigned(id, selectedFile);
      toast.success(
        lang === "en"
          ? `Signed PDF sent to ${item.email}`
          : `PDF semnat trimis la ${item.email}`,
      );
      setUploadModal(false);
      setSelectedFile(null);
      fetchItem();
    } catch (error) {
      toast.error("Failed to upload and send PDF");
    } finally {
      setUploading(false);
    }
  };

  const isValidDocumentUrl = (url) => {
    return (
      url &&
      (url.startsWith("https://") || url.startsWith("/api/")) &&
      !url.includes("mock_")
    );
  };

  const handleDownloadDocument = async (url, filename) => {
    try {
      // Validate URL format
      if (!url || url.includes("mock_")) {
        toast.error(
          lang === "en"
            ? "Document URL is invalid. This document may not have been uploaded properly."
            : "URL-ul documentului nu este valid. Documentul poate să nu fi fost încărcat corect.",
        );
        return;
      }

      // Handle Cloudinary URLs (direct HTTPS) - directly open in new tab
      if (url.startsWith("https://")) {
        window.open(url, "_blank");
        toast.success(
          lang === "en"
            ? "Document opened in new tab"
            : "Document deschis în tab nou",
        );
        return;
      }

      // Handle local backend URLs (legacy)
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
      toast.success(
        lang === "en" ? "Document downloaded" : "Document descărcat",
      );
    } catch (error) {
      toast.error(
        lang === "en"
          ? "Failed to download document"
          : "Eroare la descărcarea documentului",
      );
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const pdfBlob = await adeverintaAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Adeverinta_${item.nume}_${item.prenume}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success(lang === "en" ? "PDF downloaded" : "PDF descărcat");
    } catch (error) {
      toast.error(
        lang === "en"
          ? "Failed to download PDF"
          : "Eroare la descărcarea PDF-ului",
      );
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
            {t("admin.status.pending")}
          </span>
        );
      case "signed":
        return (
          <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
            {t("admin.status.signed")}
          </span>
        );
      case "sent":
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
            {t("admin.status.sent")}
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-stone-100 text-stone-600 rounded-full text-sm">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-stone-200 rounded w-1/4"></div>
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!item) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-stone-500">Certificate not found</p>
          <Button
            onClick={() => navigate("/admin/adeverinta")}
            className="mt-4">
            {t("common.back")}
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/adeverinta")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-stone-800">
              {item.nume} {item.prenume}
            </h1>
            <p className="text-stone-500 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {formatDate(item.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(item.status)}
          <Button
            onClick={handleDownloadPDF}
            className="bg-stone-600 hover:bg-stone-700 gap-2">
            <Download className="w-4 h-4" />
            {lang === "en" ? "Download PDF" : "Descarcă PDF"}
          </Button>
          {item.status === "pending" && (
            <Button
              onClick={() => setUploadModal(true)}
              className="bg-islamic-teal hover:bg-islamic-teal-light gap-2">
              <Upload className="w-4 h-4" />
              {lang === "en"
                ? "Upload Signed PDF & Send"
                : "Încarcă PDF semnat și trimite"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Personal Info */}
        <Card className="border-stone-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center">
                <User className="w-5 h-5 text-islamic-teal" />
              </div>
              <h2 className="font-semibold text-stone-800">
                {lang === "en"
                  ? "Personal Information"
                  : "Informații personale"}
              </h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-stone-500 uppercase">
                    {t("certificate.lastName")}
                  </p>
                  <p className="font-medium text-stone-800">{item.nume}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase">
                    {t("certificate.firstName")}
                  </p>
                  <p className="font-medium text-stone-800">{item.prenume}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase">
                  {t("certificate.cnp")}
                </p>
                <p className="font-mono text-stone-800">{item.cnp}</p>
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase">
                  {t("certificate.nationality")}
                </p>
                <p className="text-stone-800">{item.cetatenie}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employer Info */}
        <Card className="border-stone-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-islamic-gold/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-islamic-gold" />
              </div>
              <h2 className="font-semibold text-stone-800">
                {lang === "en"
                  ? "Employer Information"
                  : "Informații angajator"}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-stone-500 uppercase">
                  {t("certificate.employerName")}
                </p>
                <p className="font-medium text-stone-800">
                  {item.employer_name}
                </p>
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase">
                  {t("certificate.employerAddress")}
                </p>
                <p className="text-stone-800">{item.employer_address}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="border-stone-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-semibold text-stone-800">
                {t("certificate.step3")}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-stone-500 uppercase">
                  {t("certificate.email")}
                </p>
                <a
                  href={`mailto:${item.email}`}
                  className="font-medium text-islamic-teal hover:underline">
                  {item.email}
                </a>
              </div>
              {item.phone && (
                <div>
                  <p className="text-xs text-stone-500 uppercase">
                    {t("certificate.phone")}
                  </p>
                  <a
                    href={`tel:${item.phone}`}
                    className="text-stone-800 hover:text-islamic-teal">
                    {item.phone}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="border-stone-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-stone-600" />
              </div>
              <h2 className="font-semibold text-stone-800">
                {t("certificate.step4")}
              </h2>
            </div>

            <div className="space-y-4">
              {item.attachments?.id_card_url ? (
                isValidDocumentUrl(item.attachments.id_card_url) ? (
                  <button
                    onClick={() =>
                      handleDownloadDocument(
                        item.attachments.id_card_url,
                        `ID_Card_${item.nume}_${item.prenume}`,
                      )
                    }
                    className="w-full flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors cursor-pointer">
                    <Download className="w-5 h-5 text-islamic-teal" />
                    <span className="text-sm">
                      {lang === "en"
                        ? "ID Card / Passport"
                        : "Carte de identitate / Pașaport"}
                    </span>
                  </button>
                ) : (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-700">
                      {lang === "en"
                        ? "ID Card / Passport - Document not properly uploaded"
                        : "Carte de identitate / Pașaport - Document nu a fost încărcat corect"}
                    </p>
                  </div>
                )
              ) : (
                <p className="text-sm text-stone-500">
                  {lang === "en"
                    ? "No ID uploaded"
                    : "Nicio carte de identitate încărcată"}
                </p>
              )}

              {item.attachments?.islamic_cert_url &&
                (isValidDocumentUrl(item.attachments.islamic_cert_url) ? (
                  <button
                    onClick={() =>
                      handleDownloadDocument(
                        item.attachments.islamic_cert_url,
                        `Islamic_Cert_${item.nume}_${item.prenume}`,
                      )
                    }
                    className="w-full flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors cursor-pointer">
                    <Download className="w-5 h-5 text-islamic-gold" />
                    <span className="text-sm">
                      {lang === "en"
                        ? "Islamic Certificate"
                        : "Certificat islamic"}
                    </span>
                  </button>
                ) : (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-700">
                      {lang === "en"
                        ? "Islamic Certificate - Document not properly uploaded"
                        : "Certificat islamic - Document nu a fost încărcat corect"}
                    </p>
                  </div>
                ))}

              {item.signed_pdf_url && (
                <a
                  href={item.signed_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium text-teal-700">
                    {lang === "en"
                      ? "Signed PDF (Sent)"
                      : "PDF semnat (Trimis)"}
                  </span>
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lang === "en" ? "Upload Signed PDF" : "Încarcă PDF semnat"}
            </DialogTitle>
            <DialogDescription>
              {lang === "en"
                ? `The signed PDF will be sent to ${item?.email}`
                : `PDF-ul semnat va fi trimis la ${item?.email}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="signed_pdf">
                {lang === "en" ? "Select PDF file" : "Selectează fișierul PDF"}
              </Label>
              <div className="mt-2">
                <label
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    selectedFile
                      ? "border-islamic-teal bg-islamic-teal/5"
                      : "border-stone-300 hover:border-islamic-teal hover:bg-stone-50"
                  }`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {selectedFile ? (
                      <>
                        <CheckCircle className="w-8 h-8 text-islamic-teal mb-2" />
                        <p className="text-sm text-islamic-teal font-medium">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-stone-400 mb-2" />
                        <p className="text-sm text-stone-500">
                          {lang === "en"
                            ? "Click to select PDF"
                            : "Click pentru a selecta PDF"}
                        </p>
                        <p className="text-xs text-stone-400">
                          PDF only, max 10MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    id="signed_pdf"
                    className="hidden"
                    accept=".pdf,application/pdf"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <Send className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">
                    {lang === "en"
                      ? "Email will be sent to:"
                      : "Email-ul va fi trimis la:"}
                  </p>
                  <p>{item?.email}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModal(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleUploadAndSend}
              disabled={!selectedFile || uploading}
              className="bg-islamic-teal hover:bg-islamic-teal-light gap-2">
              {uploading ? (
                <>{t("common.loading")}</>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {lang === "en" ? "Upload & Send" : "Încarcă și trimite"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAdeverintaDetail;
